import React from 'react';
import Proptypes from 'prop-types';
import style from './index.scss';

import {
  Button,
  Divider,
  Input,
  message,
  Modal,
  Pagination,
  Select,
  notification
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import {
  addTypologyToMapping,
  clearUploadTypologies,
  createSingleScenario,
  deleteSingleScenario,
  fetchAllScenarios,
  triggerScenarios,
  updateSingleScenario,
  createSingleScenarioReset,
  hideValidationErrors
} from '../../../../redux/actions/trm/trmDashboard';
import ScenarioValidationErrorModal from '../../configureInstanceDashboard/ScenarioValidationErrorModal';

import { getStatusForScenario } from '../../trmUtil';

import { find, trim } from '../../../../util/lodashlike';
const { Option } = Select;
const defaultState = {
  isModalVisible: false,
  typologyObject: {},
  isCreateNewRunInstanceClicked: false,
  selectedScenarios: [],
  newTypologyText: '',
  isNewlyCreatedScenarioAdded: false,
  loadingState: false,
  pagination: {
    page: 1,
    pageSize: 20
  },
  allScenariosList: []
};
/**
 * RunInstanceHandler UI Class
 */
class RunInstanceHandler extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...defaultState
    };
  }

  componentDidMount() {
    // Modal uses destroyOnClose so this fires on every open. Clear stale
    // createdScenario state from any prior open — otherwise the auto-add
    // path in UNSAFE_componentWillReceiveProps re-adds the previous
    // session's id, which renders as a phantom empty tag in the Select
    // (the id isn't in the current typology's scenario list, so AntD has
    // no label to show) and Confirm submits an empty selection.
    this.props.dispatchCreateScenarioReset({});
    const { typologyObject } = this.props;
    const { page, pageSize } = this.state.pagination;
    if (typologyObject) {
      const { sourceId = '' } = typologyObject;
      this.props.dispatchFetchAllScenarioAPI({
        page,
        pageSize,
        sourceId
      });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      trmDashboard = {},
      isModalVisible = false,
      typologyObject = {}
    } = nextProps;
    if (trmDashboard) {
      const {
        allScenarios = {},
        typologyScenarioMapping = {},
        createdScenario = {}
      } = trmDashboard || {};

      const { data = {} } = allScenarios || {};
      const { metadata = {}, scenarios = [] } = data || {};

      this.setState({
        isModalVisible: isModalVisible,
        typologyObject: typologyObject,
        // FIN-12269: de-dupe by id. This runs on every prop change and a refetch
        // (e.g. post-create) re-delivers page-1, so a plain concat rendered the
        // same run instance twice in the "Add to" dropdown.
        allScenariosList: [
          ...new Map(
            [...this.state.allScenariosList, ...scenarios].map((s) => [s.id, s])
          ).values()
        ],
        scenariosMetaData: metadata
      });
      // Only auto-add the freshly created scenario when this open of the
      // modal is the one that created it. Without isCreateNewRunInstanceClicked
      // the guard, a stale createdScenario.data id from a prior session would
      // get pushed into selectedScenarios on next open and render as a
      // phantom empty tag in the Select (id not in this typology's scenario
      // list, so AntD has no label to show).
      if (
        createdScenario.data &&
        !createdScenario.isLoading &&
        !createdScenario?.data?.message &&
        !this.state.isNewlyCreatedScenarioAdded &&
        this.state.isCreateNewRunInstanceClicked &&
        typologyObject
      ) {
        const { sourceId = '' } = typologyObject;
        this.setState({
          selectedScenarios: this.state.selectedScenarios.concat(
            createdScenario.data.id
          ),
          isNewlyCreatedScenarioAdded: true,
          isCreateNewRunInstanceClicked: false
        });
        this.props.dispatchFetchAllScenarioAPI({ sourceId });
      } else {
        if (createdScenario?.data?.message) {
          message.error(createdScenario.data.message);
        }
      }
    }
  }

  /**
   * toggleModal
   */
  toggleModal = () => {
    this.setState({ isModalVisible: !this.state.isModalVisible });
  };

  validateTypologiesStatus = () => {
    let completedTypologies = this.state.allScenariosList.filter(
      ({ id, latestInstance }) =>
        this.state.selectedScenarios.includes(id) &&
        ['Completed', 'Failed', 'InProgress'].includes(latestInstance?.status)
    );

    if (completedTypologies?.length) {
      notification.warning({
        description:
          'The configuration setting has been made blank and the user needs to re-enter the details and configuration.'
      });
    }
  };

  /**
   * handleOk
   * @returns {Promise<void>}
   */
  handleOk = async () => {
    if (this.state.selectedScenarios.length > 0) {
      this.validateTypologiesStatus();
      await this.props.dispatchAddTypologyToScenarioAPI({
        typologyIds: [this.state.typologyObject.id],
        scenarioIds: this.state.selectedScenarios
      });
      message.success(`Typology added to run instance/s successfully.`);
      this.props.onClose();
      // NB: do NOT dispatch clearUploadTypologies here. That action resets
      // the entire trmDashboard slice (including scenarioTypologies.data),
      // blanking the Configure Scenario page on Confirm. Parent owns
      // post-confirm cleanup via onClose().
    } else {
      message.error(`At-least 1 scenario should be created/selected.`);
    }
  };

  /**
   * handleCancel
   */
  handleCancel = () => {
    this.props.dispatchCreateScenarioReset({});
    this.toggleModal();
    this.props.onClose();
  };

  /**
   * onCancelCreateNewRunInstance
   */
  onCancelCreateNewRunInstance = () => {
    this.setState({
      isCreateNewRunInstanceClicked: false,
      newTypologyText: ''
    });
  };

  /**
   * onNewTypologyText
   * @param event
   */
  onNewTypologyText = (event) => {
    this.setState({ newTypologyText: event.target.value });
  };

  /**
   * onConfirmCreateNewRunInstance
   * @returns {Promise<void>}
   */
  onConfirmCreateNewRunInstance = async () => {
    const { typologyObject = {} } = this.props;
    const { sourceId = '' } = typologyObject;

    if (
      this.state.newTypologyText &&
      this.state.newTypologyText.length > 5 &&
      this.state.newTypologyText.length < 50 &&
      trim(this.state.newTypologyText).length > 5
    ) {
      await this.props.dispatchCreateScenarioAPI({
        name: trim(this.state.newTypologyText),
        sourceId
      });
      this.setState({ newTypologyText: '' });
    } else {
      message.error('Length should be between 5 and 50');
    }
  };

  /**
   * onCreateNewScenarioClick
   */
  onCreateNewScenarioClick = () => {
    this.setState({
      isCreateNewRunInstanceClicked: true,
      isNewlyCreatedScenarioAdded: false
    });
  };

  /**
   * onScenarioSelect
   * @param scenarios
   */
  onScenarioSelect = (scenarios) => {
    this.setState({ selectedScenarios: scenarios });
  };

  loadMore = (e) => {
    e.persist();
    const { target } = e;
    const { trmDashboard = {} } = this.props;
    const { allScenarios = {} } = trmDashboard || {};
    const { data = {} } = allScenarios || {};
    const { metadata = {} } = data || {};
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      metadata &&
      this.state.allScenariosList.length < metadata.total
    ) {
      this.setState({ loadingState: true });
      const { typologyObject } = this.props;
      const { page, pageSize } = this.state.pagination;
      const { sourceId = '' } = typologyObject;
      this.setState(
        {
          pagination: {
            page: page + 1,
            pageSize: 20
          }
        },
        () => {
          this.props.dispatchFetchAllScenarioAPI({
            page: page + 1,
            pageSize,
            sourceId
          });
        }
      );
    }
  };

  render() {
    const {
      typologyObject = {},
      isModalVisible,
      allScenariosList
    } = this.state;
    const { trmDashboard = {}, dispatchHideValidationErrors } = this.props;
    const { allScenarios = {}, scenarioValidationErrors = {} } =
      trmDashboard || {};
    const { isLoading = true } = allScenarios || {};
    const { title = '' } = typologyObject;
    return (
      <>
        {/* FIN-11803: Launch-gate modal — second entry point. Same saga
            (FIN-11800) gates this Launch path too via the shared
            scenarioValidationErrors slice of trmDashboard. */}
        <ScenarioValidationErrorModal
          visible={!!scenarioValidationErrors.visible}
          errors={scenarioValidationErrors.errors || []}
          onOk={dispatchHideValidationErrors}
        />
        <Modal
          destroyOnClose={true}
          title="Add to Run Instance"
          visible={isModalVisible}
          width={634}
          bodyStyle={{ height: 377 }}
          onOk={this.handleOk}
          onCancel={this.handleCancel}
          footer={[
            <Button key="back" onClick={this.handleCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={this.handleOk}>
              Confirm
            </Button>
          ]}>
          <div className={style.typologyNameContainer}>
            <p>Typology Name:</p>
            <p>{title}</p>
          </div>

          <div className={style.scenarioContainer}>
            <p>Add to</p>
            <div>
              <Select
                showSearch
                onChange={(e) => {
                  this.onScenarioSelect(e);
                }}
                loading={isLoading}
                mode="multiple"
                value={this.state.selectedScenarios}
                style={{ width: 430 }}
                placeholder="Search to Select"
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const { name = '' } = find(allScenariosList, [
                    'id',
                    option.value
                  ]);
                  return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                onPopupScroll={this.loadMore}>
                {allScenariosList.map((scenarioObject) => {
                  const { id, name, latestInstance = {} } = scenarioObject;

                  return (
                    <Option key={id} value={id}>
                      <div>
                        <div>
                          {name}
                          <div className={style.scenarioStatus}>
                            ({getStatusForScenario(latestInstance?.status)})
                          </div>
                        </div>
                      </div>
                    </Option>
                  );
                })}
              </Select>
              ,
              {!this.state.isCreateNewRunInstanceClicked && (
                <div
                  onClick={this.onCreateNewScenarioClick}
                  className={style.newScenarioClass}>
                  Create a new Run Instance
                </div>
              )}
              {this.state.isCreateNewRunInstanceClicked && (
                <div>
                  <div className={style.newScenarioInputContainer}>
                    Create a new Run Instance
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Input
                      onChange={this.onNewTypologyText}
                      value={this.state.newTypologyText}
                      style={{ height: 32, width: 365, marginRight: 14 }}
                    />

                    <CloseOutlined
                      style={{ marginRight: 22, color: '#DF6057' }}
                      onClick={this.onCancelCreateNewRunInstance}
                    />

                    <CheckOutlined
                      onClick={this.onConfirmCreateNewRunInstance}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

RunInstanceHandler.propTypes = {
  dispatchCreateScenarioAPI: Proptypes.func,
  dispatchDeleteScenarioAPI: Proptypes.func,
  dispatchUpdateScenarioAPI: Proptypes.func,
  dispatchAddTypologyToScenarioAPI: Proptypes.func,
  dispatchFetchAllScenarioAPI: Proptypes.func,
  dispatchClearData: Proptypes.func
};

function mapStateToProps(state, ownProps = {}) {
  return {
    trmDashboard: state.trmDashboard
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatchCreateScenarioAPI: (payload) => {
      dispatch(createSingleScenario(payload));
    },
    dispatchCreateScenarioReset: (payload) => {
      dispatch(createSingleScenarioReset(payload));
    },
    dispatchDeleteScenarioAPI: (payload) => {
      dispatch(deleteSingleScenario(payload));
    },
    dispatchUpdateScenarioAPI: (payload) => {
      dispatch(updateSingleScenario(payload));
    },
    dispatchAddTypologyToScenarioAPI: (payload) => {
      dispatch(addTypologyToMapping(payload));
    },
    dispatchFetchAllScenarioAPI: (payload) => {
      dispatch(fetchAllScenarios(payload));
    },
    dispatchTriggerScenarioAPI: (payload) => {
      dispatch(triggerScenarios(payload));
    },
    // FIN-11803: invoked by ScenarioValidationErrorModal's OK button.
    dispatchHideValidationErrors: () => {
      dispatch(hideValidationErrors());
    },
    dispatchClearData: (payload) => {
      dispatch(clearUploadTypologies(payload));
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RunInstanceHandler);
